import { useState } from 'react';
import api from '../../services/api';

export default function AddTeacherModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    phone_number: '',
    first_name: '',
    last_name: '',
    coaching_institute_name: '',
    coaching_institute_location: '',
    subjects: ['physics', 'chemistry', 'mathematics'],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    console.log('Submitting teacher data:', formData);
    try {
      const result = await api.createTeacher(formData);
      console.log('Teacher created successfully:', result);
      onSuccess();
      onClose();
      // Reset form
      setFormData({
        email: '',
        phone_number: '',
        first_name: '',
        last_name: '',
        coaching_institute_name: '',
        coaching_institute_location: '',
        subjects: ['physics', 'chemistry', 'mathematics'],
      });
    } catch (err) {
      console.error('Failed to create teacher:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6 border-b border-teacher-border">
          <h2 className="text-xl font-semibold text-teacher-text-primary">Add New Teacher</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="teacher@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="+919876543210 or +12025551234"
            />
            <p className="text-xs text-teacher-text-secondary mt-1">Format: +91XXXXXXXXXX (India) or +1XXXXXXXXXX (US)</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-teacher-text-primary mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-teacher-text-primary mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">
              Coaching Institute Name
            </label>
            <input
              type="text"
              name="coaching_institute_name"
              value={formData.coaching_institute_name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Elite IIT Academy"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-1">
              Location
            </label>
            <input
              type="text"
              name="coaching_institute_location"
              value={formData.coaching_institute_location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-teacher-border-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Delhi"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-teacher-text-primary mb-2">
              Subjects <span className="text-red-500">*</span>
              <span className="ml-2 text-xs font-normal text-teacher-text-secondary">
                (what this teacher teaches — defaults to all three)
              </span>
            </label>
            <div className="flex gap-2">
              {[
                { id: 'physics', label: 'Physics' },
                { id: 'chemistry', label: 'Chemistry' },
                { id: 'mathematics', label: 'Maths' },
              ].map(s => {
                const checked = formData.subjects.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      subjects: checked
                        ? formData.subjects.filter(x => x !== s.id)
                        : [...formData.subjects, s.id],
                    })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      checked
                        ? 'bg-primary-600 text-white'
                        : 'bg-teacher-bg-secondary text-teacher-text-primary hover:bg-gray-200'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
            {formData.subjects.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Select at least one subject</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-teacher-border-strong rounded-lg text-teacher-text-primary hover:bg-teacher-bg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              disabled={loading || formData.subjects.length === 0}
            >
              {loading ? 'Creating...' : 'Create Teacher'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
